import { Client } from "./clients";
import { Equipment, EquipmentCounter } from "./equipments";
import { NumerationSchema } from "./numerationSchemas";
import { CounterPriorityProviderValue } from "./tenantSettings";

/** What a contract is for. Fixed when it is created: its terms, settlements
 *  and reminders are all shaped by it, so the API refuses to change it. */
export type ContractKindValue = "R" | "S";

export const CONTRACT_KIND_RENT: ContractKindValue = "R";
export const CONTRACT_KIND_SERVICE: ContractKindValue = "S";

/** Settles exactly once - the sale of the equipment on a service contract.
 *  A sale contract takes only terms on this cycle, and a rental takes none. */
export const ONE_OFF_SETTLEMENT_CYCLE = "O";

export interface Contract {
  id: number;
  kind?: ContractKindValue;
  kind_display?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  ended?: boolean;
  indefinite_after_end_date?: boolean;
  issuance_date?: string;
  number?: string;
  internal_name?: string;
  signature_date?: string;
  inflation_condition?: string | null;
  inflation_condition_display?: string;
  inflation_rate_percent?: string | number | null;
  automation_level?: ContractAutomationLevelValue | null;
  automation_level_display?: string | null;
  counter_priority_provider?: CounterPriorityProviderValue | null;
  counter_priority_provider_display?: string | null;
  is_brutto?: boolean;
  settlement_kinds?: string[];
  settlement_kinds_display?: string[];
  has_pending_settlements?: boolean;
  terms?: ContractTerm[];
  client?: Client;
  numeration_schema?: NumerationSchema;
  created_at?: string;
  updated_at?: string;
}

export interface ExpiredContractListItem
  extends Pick<Contract, "id" | "number" | "internal_name" | "end_date" | "client"> {}

export type ContractAutomationLevelValue = "C" | "I" | "K" | "F";

export interface ContractAutomationItem {
  id: number;
  number: string;
  internal_name?: string;
  end_date: string | null;
  automation_level: ContractAutomationLevelValue | null;
  automation_level_display: string | null;
  counter_priority_provider: CounterPriorityProviderValue | null;
  counter_priority_provider_display: string | null;
  client: { id: number; name: string } | null;
}

export interface ContractAutomationLevelUpdatePayload {
  id: number;
  automation_level?: ContractAutomationLevelValue | null;
  counter_priority_provider?: CounterPriorityProviderValue | null;
}

export interface ExpiredContractProcessPayload {
  indefinite_period_contract_ids: number[];
  ended_contract_ids: number[];
}

export type ExpiredContractAction = "indefinite" | "ended";


export interface ClientContractTermPagesLimit {
  kind_display: string | null;
  included_in_rent: string;
}


export interface ClientContractTerm {
  id: string;
  equipment?: Equipment;
  contract?: Contract;
  pages_limits?: ClientContractTermPagesLimit[];
}


export interface ContractTermPrice {
  id?: string | number;
  start_counter: string | EquipmentCounter;
  end_counter: string | EquipmentCounter | null;
  price: string;
  included_in_rent: string;
  created_at: string;
  updated_at: string;
}


export interface ServiceReminderScheduleItem {
  id?: number;
  due_date: string;
  notify_at?: string;
  status?: string;
  status_display?: string;
  sent_at?: string | null;
  completed_at?: string | null;
  job?: number | null;
  job_number?: string | null;
  is_overdue?: boolean;
}

export interface ServiceReminderPlan {
  id?: number;
  is_active?: boolean;
  interval_months?: number | null;
  lead_time_days?: number;
  auto_create_job?: boolean;
  title_template?: string;
  message_template?: string;
  /** Read-only: the materialised schedule, with its status and history. */
  reminders?: ServiceReminderScheduleItem[];
  /** Write-only: the full set of still-pending dates. */
  reminder_dates?: Array<{ due_date: string }>;
}

export interface ContractTerm {
  id: string;
  active?: boolean;
  equipments?: Equipment[];
  contract: string;
  settlement_cycle: string;
  settlement_cycle_display: string;
  settlement_kind: string;
  settlement_kind_display: string;
  kind: string;
  kind_display: string;
  next_settlement_date: string | null;
  rent_price: number;
  equipment_price?: string | number | null;
  warranty_end_date?: string | null;
  service_reminder_plan?: ServiceReminderPlan | null;
  prices?: ContractTermPrice[];
  prices_info: ContractTermPrice[];
  contract_info: {
    number: string;
    internal_name?: string;
    start_date: string;
    end_date: string;
    indefinite_after_end_date?: boolean;
    is_brutto?: boolean;
  };
  client_name?: string;
  created_at: string;
  updated_at: string;
}


export interface ContractTermListPrice {
  kind_display: string | null;
  price: string;
  included_in_rent: string;
}


export interface ContractTermList {
  id: string;
  active?: boolean;
  kind_display: string;
  settlement_cycle_display: string;
  settlement_kind: string;
  settlement_kind_display: string;
  next_settlement_date: string | null;
  rent_price: number;
  equipment_price?: string | number | null;
  warranty_end_date?: string | null;
  prices_info?: ContractTermListPrice[];
  equipments: Array<{
    id?: string | number;
    name?: string;
    full_name?: string;
  }>;
  contract: {
    id: string;
    number: string;
    internal_name?: string;
    start_date: string;
    end_date: string;
    indefinite_after_end_date?: boolean;
    is_brutto?: boolean;
  };
}
